import React, { useState, useRef } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  Paper,
  CircularProgress,
  Snackbar,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { authApi } from "../apis/authApi";
import ItGlueAccessFeedbackAlert from "./ItGlueAccessFeedbackAlert";
import { buildItGlueAccessFeedback, ItGlueAccessFeedback } from "../utils/itGlueAccessChecks";
import { ITGlueApiKeyGuideSteps } from "./integrationGuides/ITGlueApiKeyGuideSteps";
import { IT_GLUE_DEFAULT_BASE_URL } from "../utils/constants";

interface ITGlueSetupFormProps {
  onBack?: () => void;
  onSuccess?: () => void;
  onSkip?: () => void;
}

const ITGlueSetupForm: React.FC<ITGlueSetupFormProps> = ({ onBack, onSuccess, onSkip }) => {
  const [apiKey, setApiKey] = useState("");
  const [baseUrl, setBaseUrl] = useState(IT_GLUE_DEFAULT_BASE_URL);
  const [isTesting, setIsTesting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [testResult, setTestResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);
  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);
  const [itGlueAccessFeedback, setItGlueAccessFeedback] = useState<ItGlueAccessFeedback | null>(null);

  // Track the values when test was successful
  const testedValuesRef = useRef<{
    apiKey: string;
    baseUrl: string;
  } | null>(null);
  
  // Check if fields have changed since successful test
  const hasFieldsChanged = () => {
    if (!testedValuesRef.current || !testResult?.success) {
      return true;
    }
    return (
      apiKey !== testedValuesRef.current.apiKey ||
      baseUrl !== testedValuesRef.current.baseUrl
    );
  };
  
  const isSaveDisabled = !testResult?.success || hasFieldsChanged() || isSaving;

  const handleTestConnection = async () => {
    if (!apiKey || !baseUrl) {
      setTestResult({
        success: false,
        message: "Please fill in all fields before testing the connection.",
      });
      return;
    }

    setIsTesting(true);
    setTestResult(null);
    setItGlueAccessFeedback(null);

    try {
      const result = await authApi.testITGlueConnection({
        api_key: apiKey,
        base_url: baseUrl,
      });

      if (result.success) {
        testedValuesRef.current = {
          apiKey,
          baseUrl,
        };
      } else {
        testedValuesRef.current = null;
      }

      setTestResult({
        success: result.success,
        message: result.message || "Connection test successful!",
      });
      if (result.details?.permission_checks?.length) {
        setItGlueAccessFeedback(buildItGlueAccessFeedback(result));
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || error.message || "Connection test failed. Please check your credentials.";
      setTestResult({
        success: false,
        message: errorMessage,
      });
      testedValuesRef.current = null;
      setItGlueAccessFeedback(null);
    } finally {
      setIsTesting(false);
    }
  };

  const handleSave = async () => {
    if (isSaveDisabled) {
      return;
    }

    setIsSaving(true);

    try {
      await authApi.saveITGlueConfiguration({
        api_key: apiKey,
        base_url: baseUrl,
      });

      // Show success message
      setShowSuccessSnackbar(true);
      
      // Call onSuccess callback if provided
      if (onSuccess) {
        setTimeout(() => {
          onSuccess();
        }, 1500);
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || error.message || "Failed to save IT Glue configuration.";
      setTestResult({
        success: false,
        message: errorMessage,
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box>
      {onBack && (
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={onBack}
          sx={{
            fontFamily: "var(--theo-font-sans)",
            color: "#6c6c6c",
            textTransform: "none",
            mb: 3,
            "&:hover": {
              background: "#f5f5f5",
            },
          }}
        >
          Back
        </Button>
      )}

      <Typography
        variant="h5"
        component="h2"
        sx={{
          fontFamily: "var(--theo-font-sans)",
          fontWeight: 700,
          mb: 3,
          color: "#1a1a1a",
        }}
      >
        IT Glue Setup
      </Typography>

      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 4,
          background: "#f8fafc",
          borderRadius: 2,
          border: "1px solid #e5e5e5",
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontFamily: "var(--theo-font-sans)",
            fontWeight: 600,
            mb: 2,
            color: "#1a1a1a",
          }}
        >
          How to get an API key
        </Typography>
        <Box sx={{ fontFamily: "var(--theo-font-sans)", color: "#6c6c6c", pl: 0 }}>
          <ITGlueApiKeyGuideSteps
            showTitle={false}
            listSx={{
              pl: 3,
              "& li": { mb: 1.5, lineHeight: 1.6 },
            }}
          />
        </Box>
      </Paper>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <TextField
          label="Base URL"
          value={baseUrl}
          onChange={(e) => {
            setBaseUrl(e.target.value);
            if (testResult?.success) {
              setTestResult(null);
              setItGlueAccessFeedback(null);
            }
          }}
          placeholder={IT_GLUE_DEFAULT_BASE_URL}
          fullWidth
          required
          sx={{
            fontFamily: "var(--theo-font-sans)",
            "& .MuiInputLabel-root": {
              fontFamily: "var(--theo-font-sans)",
            },
            "& .MuiInputBase-input": {
              fontFamily: "var(--theo-font-sans)",
            },
          }}
          helperText="Your IT Glue API base URL"
        />

        <TextField
          label="API Key"
          type="password"
          value={apiKey}
          onChange={(e) => {
            setApiKey(e.target.value);
            if (testResult?.success) {
              setTestResult(null);
              setItGlueAccessFeedback(null);
            }
          }}
          fullWidth
          required
          sx={{
            fontFamily: "var(--theo-font-sans)",
            "& .MuiInputLabel-root": {
              fontFamily: "var(--theo-font-sans)",
            },
            "& .MuiInputBase-input": {
              fontFamily: "var(--theo-font-sans)",
            },
          }}
          helperText="Your IT Glue API key"
        />

        {itGlueAccessFeedback ? (
          <ItGlueAccessFeedbackAlert
            feedback={itGlueAccessFeedback}
            connectionMessage={testResult?.message}
            onClose={() => setItGlueAccessFeedback(null)}
          />
        ) : testResult ? (
          <Alert
            severity={testResult.success ? "success" : "error"}
            sx={{
              fontFamily: "var(--theo-font-sans)",
              "& .MuiAlert-message": {
                fontFamily: "var(--theo-font-sans)",
              },
            }}
          >
            {testResult.message}
          </Alert>
        ) : null}

        <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
          <Button
            variant="outlined"
            onClick={handleTestConnection}
            disabled={isTesting || !apiKey || !baseUrl}
            sx={{
              fontFamily: "var(--theo-font-sans)",
              textTransform: "none",
              fontWeight: 500,
              borderColor: "#705d4a",
              color: "#705d4a",
              "&:hover": {
                borderColor: "#604d3a",
                background: "#f5f5f5",
              },
              minWidth: 150,
            }}
          >
            {isTesting ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                Testing...
              </>
            ) : (
              "Test Connection"
            )}
          </Button>

          <Button
            variant="contained"
            onClick={handleSave}
            disabled={isSaveDisabled}
            sx={{
              fontFamily: "var(--theo-font-sans)",
              textTransform: "none",
              fontWeight: 500,
              background: isSaveDisabled ? "#e0e0e0" : "#705d4a",
              color: isSaveDisabled ? "#9e9e9e" : "#fff",
              minWidth: 150,
              "&:hover": {
                background: isSaveDisabled ? "#e0e0e0" : "#604d3a",
              },
            }}
          >
            {isSaving ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                Saving...
              </>
            ) : (
              "Save"
            )}
          </Button>

          {onSkip && (
            <Button
              variant="text"
              onClick={onSkip}
              sx={{
                fontFamily: "var(--theo-font-sans)",
                textTransform: "none",
                fontWeight: 500,
                color: "#6c6c6c",
                "&:hover": {
                  background: "#f5f5f5",
                },
              }}
            >
              Skip for now
            </Button>
          )}
        </Box>
      </Box>

      <Snackbar
        open={showSuccessSnackbar}
        autoHideDuration={3000}
        onClose={() => setShowSuccessSnackbar(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setShowSuccessSnackbar(false)}
          severity="success"
          sx={{ fontFamily: "var(--theo-font-sans)" }}
        >
          IT Glue has been successfully connected!
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ITGlueSetupForm;
